import React, { useState, useEffect } from 'react';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';

const {
  FiX, FiLoader, FiFlag, FiTag, FiCalendar, FiFileText,
  FiUser, FiClock, FiHash, FiPercent,
} = FiIcons;

const PRIORITIES = ['low', 'medium', 'high'];
const CATEGORIES = ['release', 'promotion', 'finance', 'legal', 'event', 'other'];
const STATUSES = [
  { value: 'todo',        label: 'To Do',       color: 'border-zinc-700 text-zinc-400 bg-zinc-800/40' },
  { value: 'in_progress', label: 'In Progress',  color: 'border-brand-pink/40 text-brand-pink bg-brand-pink/10' },
  { value: 'review',      label: 'Review',       color: 'border-amber-500/40 text-amber-400 bg-amber-500/10' },
  { value: 'done',        label: 'Done',         color: 'border-green-500/40 text-green-400 bg-green-500/10' },
];

const PRIORITY_STYLES = {
  high:   'border-red-500/40 text-red-400 bg-red-500/10',
  medium: 'border-amber-500/40 text-amber-400 bg-amber-500/10',
  low:    'border-zinc-700 text-zinc-500 bg-zinc-800/40',
};

const inputCls = 'w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-sm text-white font-bold placeholder-zinc-600 focus:outline-none focus:border-brand-pink/40 transition-colors';

const Field = ({ label, icon, children }) => (
  <div className="space-y-2">
    <label className="flex items-center gap-1.5 text-[10px] font-black uppercase tracking-widest text-zinc-500">
      {icon && <SafeIcon icon={icon} className="text-[9px]" />}
      {label}
    </label>
    {children}
  </div>
);

const MilestoneModal = ({ milestone, onClose, onSave }) => {
  const isEdit = !!milestone?.id;
  const [form, setForm] = useState({
    title: '', description: '', target_date: '',
    priority: 'medium', category: 'other', notes: '',
    status: 'todo', progress_pct: 0, assignee_label: '',
    estimated_hours: '', tags: '',
  });
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');
  const [activeTab, setActiveTab] = useState('basic');

  useEffect(() => {
    if (milestone) {
      setForm({
        title:           milestone.title || '',
        description:     milestone.description || '',
        target_date:     milestone.target_date || '',
        priority:        milestone.priority || 'medium',
        category:        milestone.category || 'other',
        notes:           milestone.notes || '',
        status:          milestone.status || 'todo',
        progress_pct:    milestone.progress_pct || 0,
        assignee_label:  milestone.assignee_label || '',
        estimated_hours: milestone.estimated_hours || '',
        tags:            (milestone.tags || []).join(', '),
      });
    }
  }, [milestone]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSave = async () => {
    if (!form.title.trim()) { setErr('Title is required.'); return; }
    setLoading(true); setErr('');
    const payload = {
      ...form,
      tags: form.tags ? form.tags.split(',').map(t => t.trim()).filter(Boolean) : [],
      estimated_hours: form.estimated_hours ? parseFloat(form.estimated_hours) : 0,
      progress_pct: parseInt(form.progress_pct) || 0,
      completed: form.status === 'done',
      completed_at: form.status === 'done' && !milestone?.completed_at ? new Date().toISOString() : milestone?.completed_at || null,
    };
    const { error } = await onSave(isEdit ? milestone.id : null, payload);
    setLoading(false);
    if (error) setErr(error.message);
    else onClose();
  };

  const TABS = [
    { id: 'basic',    label: 'Basic' },
    { id: 'details',  label: 'Details' },
    { id: 'progress', label: 'Progress' },
  ];

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/80 backdrop-blur-md">
      <div className="w-full max-w-lg rounded-3xl overflow-hidden shadow-2xl"
        style={{ background: 'linear-gradient(135deg, rgba(30,20,40,0.98) 0%, rgba(20,15,35,0.99) 100%)', border: '1px solid rgba(255,102,204,0.15)' }}>

        {/* Header */}
        <div className="px-7 py-6 border-b border-white/8"
          style={{ background: 'linear-gradient(90deg, rgba(255,102,204,0.08) 0%, rgba(255,102,0,0.05) 100%)' }}>
          <div className="flex items-center justify-between mb-5">
            <div>
              <p className="text-[10px] font-black uppercase tracking-widest text-brand-pink/60">{isEdit ? 'Edit' : 'New'}</p>
              <h3 className="text-xl font-black text-white uppercase tracking-tight">Milestone</h3>
            </div>
            <button onClick={onClose}
              className="p-2 rounded-xl border border-white/10 text-zinc-400 hover:text-white hover:border-brand-pink/40 transition-all">
              <SafeIcon icon={FiX} className="text-lg" />
            </button>
          </div>

          {/* Tabs */}
          <div className="flex gap-1">
            {TABS.map(t => (
              <button key={t.id} onClick={() => setActiveTab(t.id)}
                className={`px-4 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all ${
                  activeTab === t.id
                    ? 'bg-gradient-to-r from-brand-pink to-brand-coral text-white shadow-[0_0_12px_rgba(255,102,204,0.3)]'
                    : 'text-zinc-600 hover:text-zinc-300'
                }`}>
                {t.label}
              </button>
            ))}
          </div>
        </div>

        {/* Body */}
        <div className="p-7 space-y-5 max-h-[55vh] overflow-y-auto">
          {/* ── Basic Tab ── */}
          {activeTab === 'basic' && (
            <div className="space-y-5">
              <Field label="Title *">
                <input className={inputCls} placeholder="e.g. Release first single"
                  value={form.title} onChange={e => set('title', e.target.value)} />
              </Field>

              <Field label="Description">
                <textarea className={`${inputCls} resize-none`} rows={2}
                  placeholder="What needs to happen?"
                  value={form.description} onChange={e => set('description', e.target.value)} />
              </Field>

              <div className="grid grid-cols-2 gap-4">
                <Field label="Priority" icon={FiFlag}>
                  <div className="flex flex-col gap-1.5">
                    {PRIORITIES.map(p => (
                      <button key={p} type="button" onClick={() => set('priority', p)}
                        className={`px-3 py-2 rounded-xl border text-[10px] font-black uppercase tracking-widest transition-all ${
                          form.priority === p ? PRIORITY_STYLES[p] : 'border-white/8 text-zinc-600 hover:border-white/15'
                        }`}>
                        {p}
                      </button>
                    ))}
                  </div>
                </Field>

                <Field label="Category" icon={FiTag}>
                  <div className="flex flex-col gap-1.5">
                    {CATEGORIES.map(c => (
                      <button key={c} type="button" onClick={() => set('category', c)}
                        className={`px-3 py-2 rounded-xl border text-[10px] font-black uppercase tracking-widest transition-all text-left ${
                          form.category === c
                            ? 'border-brand-coral/40 text-brand-coral bg-brand-coral/10'
                            : 'border-white/8 text-zinc-600 hover:border-white/15'
                        }`}>
                        {c}
                      </button>
                    ))}
                  </div>
                </Field>
              </div>

              <Field label="Target Date" icon={FiCalendar}>
                <input type="date" className={inputCls}
                  value={form.target_date} onChange={e => set('target_date', e.target.value)} />
              </Field>
            </div>
          )}

          {/* ── Details Tab ── */}
          {activeTab === 'details' && (
            <div className="space-y-5">
              <Field label="Status">
                <div className="grid grid-cols-2 gap-2">
                  {STATUSES.map(s => (
                    <button key={s.value} type="button" onClick={() => set('status', s.value)}
                      className={`px-3 py-2.5 rounded-xl border text-[10px] font-black uppercase tracking-widest transition-all ${
                        form.status === s.value ? s.color : 'border-white/8 text-zinc-600 hover:border-white/15'
                      }`}>
                      {s.label}
                    </button>
                  ))}
                </div>
              </Field>

              <Field label="Assignee" icon={FiUser}>
                <input className={inputCls} placeholder="e.g. You / Partner"
                  value={form.assignee_label} onChange={e => set('assignee_label', e.target.value)} />
              </Field>

              <Field label="Estimated Hours" icon={FiClock}>
                <input type="number" min="0" step="0.5" className={inputCls}
                  placeholder="e.g. 8"
                  value={form.estimated_hours} onChange={e => set('estimated_hours', e.target.value)} />
              </Field>

              <Field label="Tags (comma-separated)" icon={FiHash}>
                <input className={inputCls} placeholder="e.g. launch, urgent, q3"
                  value={form.tags} onChange={e => set('tags', e.target.value)} />
              </Field>

              <Field label="Notes" icon={FiFileText}>
                <textarea className={`${inputCls} resize-none`} rows={3}
                  placeholder="Additional context or requirements..."
                  value={form.notes} onChange={e => set('notes', e.target.value)} />
              </Field>
            </div>
          )}

          {/* ── Progress Tab ── */}
          {activeTab === 'progress' && (
            <div className="space-y-6">
              <Field label="Progress" icon={FiPercent}>
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-black text-zinc-400">Completion</span>
                    <span className="text-xl font-black text-brand-pink">{form.progress_pct}%</span>
                  </div>
                  <input type="range" min="0" max="100" step="5"
                    value={form.progress_pct}
                    onChange={e => set('progress_pct', parseInt(e.target.value))}
                    className="w-full accent-brand-pink cursor-pointer" />
                  <div className="h-3 rounded-full bg-white/5 overflow-hidden">
                    <div className="h-full rounded-full bg-gradient-to-r from-brand-pink to-brand-coral transition-all duration-300"
                      style={{ width: `${form.progress_pct}%` }} />
                  </div>
                  <div className="grid grid-cols-5 gap-1">
                    {[0, 25, 50, 75, 100].map(v => (
                      <button key={v} type="button" onClick={() => set('progress_pct', v)}
                        className={`py-1.5 rounded-lg border text-[10px] font-black uppercase tracking-widest transition-all ${
                          form.progress_pct === v
                            ? 'border-brand-pink/40 text-brand-pink bg-brand-pink/10'
                            : 'border-white/8 text-zinc-600 hover:border-white/15'
                        }`}>
                        {v}%
                      </button>
                    ))}
                  </div>
                </div>
              </Field>

              <div className="p-4 rounded-2xl border border-white/8 bg-white/[0.03] space-y-3">
                <p className="text-[10px] font-black uppercase tracking-widest text-zinc-500">Status Preview</p>
                <div className="flex items-center gap-3">
                  <div className={`w-3 h-3 rounded-full ${
                    form.status === 'done' ? 'bg-green-500' :
                    form.status === 'in_progress' ? 'bg-brand-pink' :
                    form.status === 'review' ? 'bg-amber-400' : 'bg-zinc-600'
                  }`} />
                  <span className="text-sm font-black text-white capitalize">{form.status.replace('_', ' ')}</span>
                  {form.progress_pct > 0 && (
                    <span className="text-xs font-black text-brand-pink">{form.progress_pct}% done</span>
                  )}
                </div>
                {form.target_date && (
                  <p className="text-xs font-bold text-zinc-500">
                    Due: {new Date(form.target_date).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}
                  </p>
                )}
              </div>
            </div>
          )}

          {err && <p className="text-red-400 text-xs font-black">{err}</p>}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-7 py-5 border-t border-white/8">
          <button onClick={onClose}
            className="px-5 py-3 border border-white/10 rounded-2xl text-xs font-black uppercase tracking-widest text-zinc-400 hover:border-white/20 hover:text-white transition-all">
            Cancel
          </button>
          <button onClick={handleSave} disabled={loading}
            className="flex items-center gap-2 px-6 py-3 text-xs font-black uppercase tracking-widest rounded-2xl transition-all disabled:opacity-50 hover:scale-105 active:scale-95 text-white shadow-[0_0_16px_rgba(255,102,204,0.3)]"
            style={{ background: 'linear-gradient(135deg, #ff66cc, #ff6600)' }}>
            {loading && <SafeIcon icon={FiLoader} className="animate-spin text-sm" />}
            {isEdit ? 'Save Changes' : 'Add Milestone'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default MilestoneModal;